import { useCallback, useEffect, useMemo, useState } from 'react';
import bs58 from 'bs58';
import { PublicKey } from '@solana/web3.js';
import { useConnectWallet, useLinkAccount, usePrivy } from '@privy-io/react-auth';
import { useWallets as useSolanaWallets } from '@privy-io/react-auth/solana';
import { useWallet } from './useWallet';
import { CHAIN_KEYS } from '../chains/interfaces';
import { getChainConfig } from '../chains/registry';

const getSolanaChainId = chain => {
  const cluster = String(chain?.networkId || 'mainnet-beta').toLowerCase();
  if (cluster.includes('devnet')) return 'solana:devnet';
  if (cluster.includes('testnet')) return 'solana:testnet';
  return 'solana:mainnet';
};

const formatSolanaSignature = signature => {
  if (typeof signature === 'string') return signature;
  return bs58.encode(Buffer.from(signature));
};

const createPrivySolanaWalletAdapter = ({ wallet, chain }) => {
  if (!wallet?.address) return null;

  const chainId = getSolanaChainId(chain);
  const publicKey = new PublicKey(wallet.address);
  const name = wallet.standardWallet?.name || wallet.meta?.name || 'Solana Wallet';

  return {
    publicKey,
    wallet: {
      adapter: { name },
    },
    sendTransaction: async (transaction, connection) => {
      transaction.feePayer = transaction.feePayer || publicKey;
      if (!transaction.recentBlockhash) {
        const { blockhash } = await connection.getLatestBlockhash('confirmed');
        transaction.recentBlockhash = blockhash;
      }

      const serializedTransaction = transaction.serialize({
        requireAllSignatures: false,
        verifySignatures: false,
      });

      if (wallet.signTransaction) {
        const { signedTransaction } = await wallet.signTransaction({
          transaction: serializedTransaction,
          chain: chainId,
          options: { preflightCommitment: 'confirmed' },
        });
        return connection.sendRawTransaction(signedTransaction, {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
        });
      }

      if (wallet.signAndSendTransaction) {
        const { signature } = await wallet.signAndSendTransaction({
          transaction: serializedTransaction,
          chain: chainId,
          options: { preflightCommitment: 'confirmed' },
        });
        return formatSolanaSignature(signature);
      }

      throw new Error('Connected Solana wallet does not support transaction signing.');
    },
    disconnect: () => wallet.disconnect?.(),
  };
};

const selectSolanaWallet = wallets => (wallets.length === 1 ? wallets[0] : null);
const SOLANA_WALLET_FLOW = { walletChainType: 'solana-only' };

export const useWalletRouter = selectedChainKey => {
  const activeChain = getChainConfig(selectedChainKey);
  const evmWallet = useWallet(activeChain);
  const { wallets: solanaWallets, ready: solanaWalletsReady } = useSolanaWallets();
  const { connectWallet } = useConnectWallet();
  const { linkWallet } = useLinkAccount();
  const { ready: privyReady, authenticated } = usePrivy();
  const activeSolanaWallet = useMemo(() => selectSolanaWallet(solanaWallets), [solanaWallets]);
  const solanaWallet = useMemo(
    () => createPrivySolanaWalletAdapter({ wallet: activeSolanaWallet, chain: activeChain }),
    [activeSolanaWallet, activeChain]
  );
  const [pendingSolanaConnect, setPendingSolanaConnect] = useState(false);

  useEffect(() => {
    if (selectedChainKey !== CHAIN_KEYS.SOLANA) {
      setPendingSolanaConnect(false);
      return;
    }
    if (pendingSolanaConnect && activeSolanaWallet?.address) {
      setPendingSolanaConnect(false);
    }
  }, [activeSolanaWallet, pendingSolanaConnect, selectedChainKey]);

  const connectSolanaWallet = useCallback(async () => {
    if (activeSolanaWallet?.address) return activeSolanaWallet.address;
    if (!solanaWalletsReady) {
      throw new Error('Wallet connector is still loading. Please try again.');
    }

    setPendingSolanaConnect(true);
    if (privyReady && authenticated) {
      linkWallet(SOLANA_WALLET_FLOW);
    } else {
      connectWallet(SOLANA_WALLET_FLOW);
    }
    return null;
  }, [
    activeSolanaWallet,
    authenticated,
    connectWallet,
    linkWallet,
    privyReady,
    solanaWalletsReady,
  ]);

  const disconnectSolanaWallet = useCallback(async () => {
    if (activeSolanaWallet?.disconnect) {
      await activeSolanaWallet.disconnect();
    }
  }, [activeSolanaWallet]);

  return useMemo(() => {
    if (selectedChainKey === CHAIN_KEYS.SOLANA) {
      const walletLabel =
        activeSolanaWallet?.standardWallet?.name || activeSolanaWallet?.meta?.name || '';

      return {
        address: activeSolanaWallet?.address || '',
        isConnected: Boolean(activeSolanaWallet?.address),
        isCorrectNetwork: true,
        currentChainId: activeChain?.networkId || null,
        currentNetworkName: activeChain?.networkLabel || 'Solana',
        toggleTargetNetworkName: activeChain?.networkLabel || 'Solana',
        connect: connectSolanaWallet,
        disconnect: disconnectSolanaWallet,
        switchNetwork: async () => ({
          success: false,
          alreadyOnNetwork: true,
          targetNetworkName: activeChain?.networkLabel || 'Solana',
        }),
        checkNetwork: async () => true,
        provider: null,
        solanaWallet,
        walletProviderLabel: walletLabel,
        chainKey: CHAIN_KEYS.SOLANA,
      };
    }

    return {
      ...evmWallet,
      chainKey: selectedChainKey,
    };
  }, [
    selectedChainKey,
    evmWallet,
    activeSolanaWallet,
    activeChain,
    connectSolanaWallet,
    disconnectSolanaWallet,
    solanaWallet,
  ]);
};
